import Permissions from './pages/Permissions';
import { homeForUser } from './lib/permissions';
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import Navigation from './components/Navigation';
import TopNavigation from './components/TopNavigation';
import RoleBasedRoute from './components/RoleBasedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Customers from './pages/Customers';
import Orders from './pages/Orders';
import CreateOrder from './pages/CreateOrder';
import OrderEdit from './pages/OrderEdit';
import SalesDashboard from './pages/SalesDashboard';
import Shops from './pages/Shops';
import Perfumes from './pages/Perfumes';
// SMART BOTTLING - COMMENTED OUT (Advanced recipe-based bottling system)
// Features: Recipe management, raw materials inventory, forecasting, analytics
// Uncomment if you need: Production-scale operations, detailed cost tracking, material forecasting
// import SmartBottling from './pages/SmartBottling';
import Production from './pages/Production';
import Procurement from './pages/Procurement';
import Notifications from './pages/Notifications';
import Integrations from './pages/Integrations';
import Inventory from './pages/Inventory';
import Stocks from './pages/Stocks';
import OutOfStockSales from './pages/OutOfStockSales';
import ReceivedCheck from './pages/ReceivedCheck';
import Loyalty from './pages/Loyalty';
import Layaway from './pages/Layaway';
import Expenses from './pages/Expenses';
import GLAccounts from './pages/GLAccounts';
import Analytics from './pages/Analytics';
import IncomeReport from './pages/IncomeReport';
import Settings from './pages/Settings';
import Categories from './pages/Categories';
import Brands from './pages/Brands';
import Discounts from './pages/Discounts';
import PricingManagement from './pages/PricingManagement';
import GiftCards from './pages/GiftCards';
import './index.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Layout Component
const Layout = ({ children }) => {
  const { isDesktopSidebarOpen } = useSidebar();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className={`transition-all duration-300 ease-in-out ${isDesktopSidebarOpen ? 'lg:pl-64' : 'lg:pl-0'}`}>
        <TopNavigation />
        <main className="flex-1 px-2 sm:px-4 lg:px-6 xl:px-8 pb-4 sm:pb-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// Main App Component
const AppContent = () => {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          user ? (
            <Navigate to={homeForUser(user)} replace />
          ) : <Login />
        }
      />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute><Navigate to={homeForUser(user)} replace /></ProtectedRoute>
      } />

      <Route
        path="/dashboard"
        element={
          <RoleBasedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/products"
        element={
          <RoleBasedRoute>
            <Layout>
              <Products />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/categories"
        element={
          <RoleBasedRoute>
            <Layout>
              <Categories />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/brands"
        element={
          <RoleBasedRoute>
            <Layout>
              <Brands />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/customers"
        element={
          <RoleBasedRoute>
            <Layout>
              <Customers />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/sales-dashboard"
        element={
          <RoleBasedRoute>
            <Layout>
              <SalesDashboard />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/orders"
        element={
          <RoleBasedRoute>
            <Layout>
              <Orders />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/orders/create"
        element={
          <RoleBasedRoute>
            <CreateOrder />
          </RoleBasedRoute>
        }
      />

      <Route
        path="/orders/edit/:orderId"
        element={
          <RoleBasedRoute>
            <Layout>
              <Orders />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/shops"
        element={
          <RoleBasedRoute>
            <Layout>
              <Shops />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/perfumes"
        element={
          <RoleBasedRoute>
            <Layout>
              <Perfumes />
            </Layout>
          </RoleBasedRoute>
        }
      />

      {/* SMART BOTTLING ROUTE - COMMENTED OUT */}
      {/* Uncomment the route below to enable Smart Bottling system */}
      {/* 
      <Route 
        path="/smart-bottling" 
        element={
          <RoleBasedRoute>
            <Layout>
              <SmartBottling />
            </Layout>
          </RoleBasedRoute>
        } 
      />
      */}

      <Route
        path="/production"
        element={
          <RoleBasedRoute>
            <Layout>
              <Production />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/procurement"
        element={
          <RoleBasedRoute>
            <Layout>
              <Procurement />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/production/:tab"
        element={
          <RoleBasedRoute>
            <Layout>
              <Production />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/inventory"
        element={
          <RoleBasedRoute>
            <Layout>
              <Inventory />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/inventory/:tab"
        element={
          <RoleBasedRoute>
            <Layout>
              <Inventory />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/stocks"
        element={
          <RoleBasedRoute>
            <Layout>
              <Stocks />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/out-of-stock-sales"
        element={
          <RoleBasedRoute>
            <Layout>
              <OutOfStockSales />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/received-check"
        element={
          <RoleBasedRoute>
            <Layout>
              <ReceivedCheck />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/notifications"
        element={
          <RoleBasedRoute>
            <Layout>
              <Notifications />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/integrations"
        element={
          <RoleBasedRoute>
            <Layout>
              <Integrations />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/loyalty"
        element={
          <RoleBasedRoute>
            <Layout>
              <Loyalty />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/layaway"
        element={
          <RoleBasedRoute>
            <Layout>
              <Layaway />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/discounts"
        element={
          <RoleBasedRoute>
            <Layout>
              <Discounts />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/expenses"
        element={
          <RoleBasedRoute>
            <Layout>
              <Expenses />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/gl-accounts"
        element={
          <RoleBasedRoute>
            <Layout>
              <GLAccounts />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/analytics"
        element={
          <RoleBasedRoute>
            <Layout>
              <Analytics />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/reports/income"
        element={
          <RoleBasedRoute>
            <Layout>
              <IncomeReport />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/pricing"
        element={
          <RoleBasedRoute>
            <Layout>
              <PricingManagement />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/gift-cards"
        element={
          <RoleBasedRoute>
            <Layout>
              <GiftCards />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <RoleBasedRoute>
            <Layout>
              <Settings />
            </Layout>
          </RoleBasedRoute>
        }
      />

      <Route path="/permissions" element={<RoleBasedRoute><Layout><Permissions /></Layout></RoleBasedRoute>} />

      <Route path="/access-denied" element={
        <ProtectedRoute>
        {homeForUser(user) !== '/access-denied' ? <Navigate to={homeForUser(user)} replace /> : <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="text-center space-y-3">
            <h1 className="text-xl font-semibold">You don’t have access to this workspace</h1>
            <p className="text-gray-600">Ask your administrator to check your account role.</p>
          </div>
        </div>}
        </ProtectedRoute>
      } />
      {/* Catch all route */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Navigate to={homeForUser(user)} replace />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// Root App Component
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SidebarProvider>
          <AppContent />
        </SidebarProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App; 